import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { Toast } from './ui/Toast';
import { useToast } from '../hooks/useToast';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  role: string;
  role_id: number;
  client_id: number | null;
  establishment_id: number | null;
  is_active: number;
}

interface Role {
  id: number;
  name: string;
  description: string | null;
}

interface Client {
  id: number;
  account_name: string;
}

interface Establishment {
  id: number;
  name_establishment: string;
}

interface UserFormProps {
  user?: User | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onSuccess, onCancel }) => {
  const { token } = useAuth();
  const { can, isAdmin } = usePermissions();
  const { showToast, hideToast, toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    confirm_password: '',
    role_id: '',
    client_id: '',
    establishment_id: '',
    is_active: true
  });

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogs = async () => {
      if (!token) {
        setLoadingCatalogs(false);
        return;
      }

      setLoadingCatalogs(true);
      try {
        const [rolesRes, clientsRes, establishmentsRes] = await Promise.all([
          apiFetch('catalog/roles', {}, token),
          apiFetch('catalog/clients', {}, token),
          apiFetch('catalog/establishments', {}, token)
        ]);

        const [rolesJson, clientsJson, establishmentsJson] = await Promise.all([
          isNetworkErrorResponse(rolesRes) ? { data: { roles: [] } } : parseJsonResponse(rolesRes).catch(() => ({ data: { roles: [] } })),
          isNetworkErrorResponse(clientsRes) ? { data: { clients: [] } } : parseJsonResponse(clientsRes).catch(() => ({ data: { clients: [] } })),
          isNetworkErrorResponse(establishmentsRes) ? { data: { establishments: [] } } : parseJsonResponse(establishmentsRes).catch(() => ({ data: { establishments: [] } }))
        ]);

        if (rolesJson.data?.roles) setRoles(rolesJson.data.roles);
        if (clientsJson.data?.clients) setClients(clientsJson.data.clients);
        if (establishmentsJson.data?.establishments) setEstablishments(establishmentsJson.data.establishments);
      } catch (err) {
        console.error('Error cargando catálogos:', err);
      } finally {
        setLoadingCatalogs(false);
      }
    };

    loadCatalogs();
  }, [token]);

  // Cargar datos del usuario si está editando
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || '',
        password: '',
        confirm_password: '',
        role_id: user.role_id?.toString() || '',
        client_id: user.client_id?.toString() || '',
        establishment_id: user.establishment_id?.toString() || '',
        is_active: user.is_active === 1
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'El nombre es requerido';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'El apellido es requerido';
    }

    if (!formData.email.trim()) {
      errors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'El email no es válido';
    }

    if (!formData.username.trim()) {
      errors.username = 'El nombre de usuario es requerido';
    }

    if (!user && !formData.password) {
      errors.password = 'La contraseña es requerida';
    }

    if (formData.password && formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Las contraseñas no coinciden';
    }

    if (!formData.role_id) {
      errors.role_id = 'El rol es requerido';
    }

    // Validar que solo admin puede crear otros admins
    if (formData.role_id) {
      const selectedRole = roles.find(r => r.id.toString() === formData.role_id);
      if (selectedRole?.name === 'admin' && !isAdmin) {
        errors.role_id = 'Solo los administradores pueden crear usuarios con rol admin';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('No hay token de autenticación');
      showToast('No hay token de autenticación', 'error');
      return;
    }

    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      showToast('Por favor, corrige los errores en el formulario', 'error');
      return;
    }

    setLoading(true);

    try {
      const isEdit = !!user;
      const route = isEdit ? 'users/update' : 'users/create';

      const payload: any = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        username: formData.username.trim(),
        role_id: parseInt(formData.role_id),
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        establishment_id: formData.establishment_id ? parseInt(formData.establishment_id) : null,
        is_active: formData.is_active ? 1 : 0
      };

      if (isEdit) {
        payload.id = user.id;
        if (formData.password) {
          payload.password = formData.password;
        }
      } else {
        payload.password = formData.password;
      }

      const res = await apiFetch(route, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, token);

      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
        showToast('Error de conexión', 'error');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al guardar usuario';
        throw new Error(errorMessage);
      }

      showToast(isEdit ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente', 'success');
      onSuccess();
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
        showToast('Error de conexión', 'error');
      } else {
        setError(err.message || 'Error inesperado');
        showToast(err.message || 'Error al guardar usuario', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingCatalogs) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
        <p className="text-gray-600">Cargando catálogos...</p>
      </div>
    );
  }

  return (
    <>
      <Toast
        message={toast.message}
        variant={toast.variant}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <div className="p-6">
        {error && (
          <div className="mb-6">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Información Personal */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Información Personal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="text"
                label="Nombre"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                error={fieldErrors.first_name}
                placeholder="Ej: Juan"
              />

              <Input
                type="text"
                label="Apellido"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                error={fieldErrors.last_name}
                placeholder="Ej: Pérez"
              />

              <Input
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={fieldErrors.email}
                placeholder="usuario@ejemplo.com"
              />

              <Input
                type="text"
                label="Nombre de Usuario"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                error={fieldErrors.username}
                placeholder="jperez"
              />
            </div>
          </div>

          {/* Contraseña */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              {user ? 'Cambiar Contraseña (opcional)' : 'Contraseña'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="password"
                label="Contraseña"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!user}
                error={fieldErrors.password}
                placeholder={user ? 'Dejar en blanco para mantener la actual' : 'Mínimo 6 caracteres'}
              />

              <Input
                type="password"
                label="Confirmar Contraseña"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                required={!user}
                error={fieldErrors.confirm_password}
                placeholder="Repite la contraseña"
              />
            </div>
          </div>

          {/* Rol y Asignaciones */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Rol y Asignaciones
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Rol"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                required
                error={fieldErrors.role_id}
                options={roles.map(r => ({ value: r.id.toString(), label: r.name }))}
              />

              <Select
                label="Cliente (opcional)"
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                options={[
                  { value: '', label: '-- Sin cliente --' },
                  ...clients.map(c => ({ value: c.id.toString(), label: c.account_name }))
                ]}
              />

              <Select
                label="Establecimiento (opcional)"
                name="establishment_id"
                value={formData.establishment_id}
                onChange={handleChange}
                options={[
                  { value: '', label: '-- Sin establecimiento --' },
                  ...establishments.map(e => ({ value: e.id.toString(), label: e.name_establishment }))
                ]}
              />
            </div>
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Usuario activo</span>
                <p className="text-xs text-gray-500 mt-0.5">Los usuarios inactivos no podrán acceder al sistema</p>
              </div>
            </label>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              isLoading={loading}
            >
              {user ? 'Actualizar Usuario' : 'Crear Usuario'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

